import projectAkira from "@/assets/project-akira.jpg";
import projectStLeonards from "@/assets/project-stleonards.jpg";
import projectHallidays from "@/assets/project-hallidays.jpg";
import projectCrescent from "@/assets/project-crescent.jpg";
import projectCessnock from "@/assets/project-cessnock.jpg";


export interface Project {
  slug: string;
  name: string;
  location: string;
  status: string;
  assetClass: string;
  isPipelineDevelopment?: boolean;
  image: string;
  externalUrl?: string;
  galleryImages?: string[];
  overview: string;
  locationContext: string;
  approach: string;
  snapshot: Record<string, string>;
  developmentApproval?: {
    description: string;
    items: string[];
    note?: string;
  };
}

export const projects: Project[] = [
  {
    slug: "akira",
    isPipelineDevelopment: true,
    name: "AKIRA",
    location: "70 Tenth Avenue, Austral, NSW",
    status: "In Development",
    assetClass: "Land Subdivision",
    image: projectAkira,
    galleryImages: [],
    overview:
      "AKIRA is a 22-lot Torrens title residential subdivision at 70 Tenth Avenue, Austral, within Sydney's South West Growth Area. The project is situated within a precinct supported by significant employment and transport investment associated with Western Sydney's long-term expansion. AKIRA represents a considered residential land release within a defined growth area.",
    locationContext:
      "Western Sydney is undergoing a major infrastructure and employment program anchored by the Western Sydney International Airport at Badgerys Creek and Bradfield City Centre within the Aerotropolis. The airport is projected to support almost 28,000 direct and indirect jobs by 2031, while Bradfield City Centre is planned to deliver approximately 10,000 new homes and 20,000 new jobs over time. Across the North West and South West Growth Areas, NSW infrastructure funding is structured to support around 125,000 new homes over the next 15 years, reinforcing the long-term housing pipeline that includes Austral and Leppington.",
    approach:
      "The site was acquired with an existing dwelling and large industrial shed, both leased to maintain holding income during the planning phase. This preserved asset productivity while the future residential use was progressed. The project has been delivered through a disciplined development framework aligned with prevailing market conditions across South West Sydney.",
    snapshot: {
      Location: "70 Tenth Avenue, Austral, NSW",
      Type: "Residential land subdivision",
      Status: "In Development",
      "Asset Class": "Residential",
    },
  },
  {
    slug: "marco",
    isPipelineDevelopment: true,
    name: "Marco",
    location: "575–583 Pacific Highway, St Leonards, NSW",
    status: "Planning",
    assetClass: "Mixed-Use",
    image: projectStLeonards,
    galleryImages: [],
    overview:
      "A mixed-use development in one of Sydney's most established and connected urban centres, bringing together residential and commercial uses.",
    locationContext:
      "St Leonards sits at the intersection of key transport routes on Sydney's Lower North Shore, with access to rail, metro, and established amenity.",
    approach:
      "Careful consideration of the urban context and existing built form, ensuring the development contributes positively to the streetscape and local community.",
    snapshot: {
      Location: "575–583 Pacific Highway, St Leonards, NSW",
      Type: "Mixed-Use",
      Status: "Planning",
      "Asset Class": "Mixed-Use",
    },
  },
  {
    slug: "hallidays-point",
    isPipelineDevelopment: true,
    name: "Hallidays Point",
    location: "166 Blackhead Road, Hallidays Point, NSW",
    status: "DA Approved",
    assetClass: "Seniors Living",
    image: projectHallidays,
    galleryImages: [],
    overview:
      "A DA-approved seniors living and healthcare precinct positioned within a strong regional catchment.\n\nThe project comprises a retirement village, residential aged care facility and medical centre, structured as an integrated seniors housing offering.\n\nThe site reflects a planning-led approach supported by favourable demographic fundamentals.",
    locationContext:
      "Hallidays Point is a coastal village located between Forster and Taree on the Mid North Coast of New South Wales.\n\nThe region demonstrates strong catchment demographics aligned with long-term demand for seniors housing and healthcare services.\n\nThe scale of the landholding allows for staged delivery within a natural coastal setting.",
    approach:
      "The project has been structured to provide planning certainty, integrated healthcare alignment, scalable delivery capability and long-term demographic support.\n\nOur focus remains disciplined planning, governance-led execution and alignment with experienced operating partners.",
    snapshot: {
      Location: "166 Blackhead Road, Hallidays Point, NSW",
      Type: "Seniors Living & Healthcare",
      Status: "DA Approved",
      "Asset Class": "Seniors Living",
    },
    developmentApproval: {
      description: "The existing development approval includes:",
      items: [
        "Retirement village with 162 independent living units (ILUs)",
        "120-bed residential aged care facility (RAC)",
        "Medical centre component",
        "32.5-hectare landholding",
      ],
      
    },
  },
  {
    slug: "the-crescent",
    isPipelineDevelopment: true,
    name: "The Crescent",
    location: "90 Baker Drive, Crescent Head, NSW",
    status: "Construction",
    assetClass: "Land Subdivision",
    image: projectCrescent,
    externalUrl: "https://www.thecrescentestate.com.au",
    galleryImages: [],
    overview:
      "In iconic Crescent Head, coastal living remains authentic.\n\nThe Crescent delivers 117 generously proportioned homesites within a village defined by community, charm and an unhurried rhythm of life. A short walk to both the beach and town centre, it is shaped by landscape-led design and connected public spaces that establish a genuine neighbourhood character.\n\nWell connected to surrounding regional centres, Crescent Head offers coastal seclusion without isolation — attracting Sydneysiders and locals seeking an enduring lifestyle base with the flexibility of short-term holiday demand.",
    locationContext:
      "Crescent Head's 3.5km coastline and world-class point break were declared a National Surfing Reserve in 2008. Framed by Goolawah Reserve and Limeburners Creek Nature Reserve, the landscape here is permanently protected - the character doesn't change.\n\nPort Macquarie, Kempsey and Coffs Harbour sit within easy reach - regional centres riding steady population growth, with strong employment across healthcare, education, construction, tourism and agriculture. The charm of a laidback surf town, backed by real economic momentum.\n\nThe village itself is evolving - good food, golf, tennis, bowls, aquatic facilities and medical amenities all close by. Lifestyle and fundamentals, in the same postcode.",
    approach:
      "LAANDED is a joint venture partner in The Crescent alongside the developer, <a href=\"https://arissa.com.au\" target=\"_blank\" rel=\"noopener noreferrer\">Arissa</a>. Our participation reflects our confidence in Arissa's capability and our belief in Crescent Head as a special regional market with enduring appeal.\n\nAs capital partner, we support the delivery of a project that aligns with our philosophy of backing quality locations, experienced operators and communities with strong long-term fundamentals.",
    snapshot: {
      Location: "90 Baker Drive, Crescent Head, NSW",
      Type: "Residential land",
      Status: "Construction",
      "Asset Class": "Land Subdivision",
    },
  },
  {
    slug: "cessnock",
    name: "Cessnock",
    location: "Cessnock, NSW",
    status: "Planning",
    assetClass: "Hotel",
    isPipelineDevelopment: false,
    image: projectCessnock,
    galleryImages: [],
    overview:
      "A boutique hospitality development in the Hunter Valley, positioned to benefit from the region's established tourism and visitor economy.",
    locationContext:
      "Cessnock serves as a gateway to the Hunter Valley wine region, one of Australia's most visited tourism destinations with strong year-round demand.",
    approach:
      "A measured approach to hospitality development, focusing on sites where tourism patterns and regional infrastructure support long-term performance.",
    snapshot: {
      Location: "Cessnock, NSW",
      Type: "Hotel",
      Status: "Planning",
      "Asset Class": "Hotel",
    },
  },
];
